import {createRef, useCallback, useContext, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  selectIsPristineLogo,
  selectIsPristineUyo,
  selectOriginalFabricArt,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {deriveIdFromCanvasObject} from '~/common/canvas/utils/canvas';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {
  UPDATE_DESIGN_OPTIONS,
  UPDATE_PRISTINE_UYO,
} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectIsFilledUyo,
  selectIsLogoFilled,
} from '~/common/fabric/reducers/fabricDesign/selectors';

export const useUyo = () => {
  const {canvas} = useContext(EventCreateContext);
  const dispatch = useDispatch();
  const pristineUyo = useSelector(selectIsPristineUyo);
  const pristineLogo = useSelector(selectIsPristineLogo);
  const canvasJson = useSelector(selectOriginalFabricArt);
  const isFilledUyo = useSelector(selectIsFilledUyo);
  const isLogoFilled = useSelector(selectIsLogoFilled);
  const isFullDyo = useSelector(({fabricTemplate}) =>
    ['general-full', 'dyo'].every((el) => fabricTemplate.templateName.includes(el))
  );
  const isUyo = !!canvasJson.objects?.find((obj) => obj.type === 'uYOImage');

  const uyoRefs = useRef([]);
  const logoRefs = useRef([]);
  if (canvas) {
    const allUyoObjects = canvas
      .getObjects()
      .filter((object) => object.type === 'uYOImage' && !!object._originalObject);

    const uyoObjects = allUyoObjects.filter((object) => !object.variant);
    const logoObjects = allUyoObjects.filter((object) => object.variant === 'logo');
    // Update the uyoRefs and logoRefs arrays to have the same length as uyoObjects and logoObjects respectively
    uyoRefs.current = uyoObjects.map((_, index) => uyoRefs.current[index] || createRef());
    logoRefs.current = logoObjects.map((_, index) => logoRefs.current[index] || createRef());
  }

  const removeUYOImage = useCallback(
    // biome-ignore lint/suspicious/noEmptyBlockStatements: can we make the default null or undefined?
    async (extraCallback = () => {}) => {
      const canvasObjects = canvas.getObjects();
      const activeObject = canvas.getActiveObject();

      if (!activeObject) return;

      if (activeObject.type === 'uYOImage' || activeObject._originalObject.type === 'uYOImage') {
        const id = activeObject.id || deriveIdFromCanvasObject(activeObject._originalObject);

        const callback = () => {
          canvas.renderAll();
          canvas.fire('object:modified');
          dispatch({
            type: UPDATE_PRISTINE_UYO,
            payload: {pristineUyo: true, id},
          });
          dispatch({
            type: UPDATE_DESIGN_OPTIONS,
            payload: {activeCardEditor: 'intro'},
          });

          extraCallback();
        };

        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {shouldUseCachedGalleryPhoto: false},
        });
        canvas.remove(activeObject);
        canvas.discardActiveObject();
        canvas.renderAll();
        canvas.fire('object:removed');

        for (const obj of canvasObjects) {
          if (
            (obj.type === 'rect' && obj.fill === 'white' && obj.id === id) ||
            obj.id === `${id}-container`
          ) {
            const variant = activeObject.variant || activeObject._originalObject.variant;
            if (variant === 'logo') {
              fabric.UYOImage.fromURL(activeObject._originalObject.src, (img) => {
                img.set({
                  variant: 'logo',
                  id,
                  hasControls: false,
                  selectable: false,
                  // biome-ignore lint/style/useNamingConvention: allow snake case
                  _originalObject: activeObject._originalObject,
                  ...activeObject._originalObject,
                });
                canvas.remove(obj);
                canvas.insertAt(img, canvas.getObjects().length);
                callback();
              });
            } else {
              obj.set({
                backgroundColor: 'rgb(128,128,128,0.5)',
                fill: 'transparent',
                type: 'uYOImage',
                id,
              });
              callback();
            }
          }
        }
      }
    },
    [canvas, dispatch]
  );

  return {
    isUyo,
    pristineUyo,
    pristineLogo,
    isFullDyo,
    isFilledUyo,
    isLogoFilled,
    uyoRefs,
    logoRefs,
    removeUYOImage,
  };
};
